<script setup lang="ts">
import type { DrawerFooterActionProps, DrawerPageProps } from './DrawerPage.vue';
import { computed, useId, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import DrawerPage from './DrawerPage.vue';

const {
	formId: formIdProp,
	cancelAction,
	submitAction,
} = defineProps<{
	title?: string;
	description?: string;
	formId?: string;
	submitAction?: DrawerFooterActionProps;
	cancelAction?: DrawerFooterActionProps;
} & Pick<DrawerPageProps, 'ui' | 'attachToRoute'>>();

/**
 * If this is any boolean value a "Create more" switch will be shown instead of the "Cancel" action
 */
const createMore = defineModel<boolean | undefined>('createMore', { default: undefined });

const { t } = useI18n();

const drawerRef = useTemplateRef('drawer');

const generatedFormId = useId();
const formId = computed(() => formIdProp ?? generatedFormId);

const footerActionItems = computed(() => {
	const actions = [
		...(createMore.value === undefined
			? [{
					label: t('common.cancel'),
					variant: 'outline',
					color: 'neutral',
					onClick: close,
					...cancelAction,
				}]
			: []),
		{
			label: t('common.save'),
			form: formId.value,
			type: 'submit',
			...submitAction,
		},
	] as DrawerFooterActionProps[];

	return actions.map((action, index) => ({
		action,
		key: `${index}-${action.type ?? 'button'}`,
	}));
});

function close() {
	drawerRef.value?.close();
}

defineExpose({
	close,
});
</script>

<template>
	<DrawerPage ref="drawer" :title :description :ui="mergeUi({ description: 'font-mono font-medium' } as DrawerPageProps['ui'], ui)">
		<slot :form-id :close />

		<template #title>
			<slot name="title" />
		</template>

		<template #footer-actions>
			<DSwitch
				v-if="createMore !== undefined"
				v-model="createMore"
				size="xs"
				:label="$t('common.create_more')"
				:ui="{ label: 'text-muted' }"
			/>
			<DButton v-for="item in footerActionItems" :key="item.key" v-bind="item.action" size="sm" />
		</template>
	</DrawerPage>
</template>
